import React, { useState, useEffect } from 'react';
import { auth, database } from '../../firebaseConfig';
import { ref, get } from 'firebase/database';
import AddCategory from './AddCategory';
import AddVideo from './AddVideo';
import MultiVideoUploader from './MultiVideoUploader';
import VideoManagementModal from './VideoManagementModal';
import CategoryManagementModal from './CategoryManagementModal';
import './Academy.css';

function Academy() {
    const [currentView, setCurrentView] = useState('overview');
    const [categories, setCategories] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [managingCategory, setManagingCategory] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);

    useEffect(() => {
        loadAcademyData();
    }, []);

    const loadAcademyData = async () => {
        if (!auth.currentUser) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        try {
            // Load categories from public Academy path
            const categoriesRef = ref(database, 'Academy/categories');
            const categoriesSnapshot = await get(categoriesRef);
            const categoriesData = [];
            if (categoriesSnapshot.exists()) {
                const categoriesObj = categoriesSnapshot.val();
                Object.entries(categoriesObj).forEach(([key, value]) => {
                    categoriesData.push({ id: key, ...value });
                });
            }
            
            // Load videos from public Academy path
            const videosRef = ref(database, 'Academy/videos');
            const videosSnapshot = await get(videosRef);
            const videosData = [];
            if (videosSnapshot.exists()) {
                const videosObj = videosSnapshot.val();
                Object.entries(videosObj).forEach(([key, value]) => {
                    videosData.push({ id: key, ...value });
                });
            }
            
            setCategories(categoriesData);
            setVideos(videosData);
        } catch (error) {
            console.error('Error loading academy data:', error);
            setCategories([]);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryAdded = (newCategory) => {
        setCategories(prev => [...prev, newCategory]);
        setCurrentView('overview');
    };

    const handleVideoAdded = (newVideo) => {
        setVideos(prev => [...prev, newVideo]);
        setCurrentView('overview');
    };

    const handleMultiUploadVideoAdded = (newVideo) => {
        setVideos(prev => [...prev, newVideo]);
    };

    const handleManageVideos = (category) => {
        setManagingCategory(category);
    };

    const handleVideosUpdated = () => {
        // Reload data when videos are updated
        loadAcademyData();
    };

    const handleEditCategory = (category) => {
        setEditingCategory(category);
    };

    const handleCategoryUpdated = () => {
        // Reload data when category is updated
        loadAcademyData();
    };

    const renderCurrentView = () => {
        switch (currentView) {
            case 'add-category':
                return (
                    <AddCategory 
                        onCategoryAdded={handleCategoryAdded}
                        onCancel={() => setCurrentView('overview')}
                    />
                );
            case 'add-video':
                return (
                    <AddVideo 
                        categories={categories}
                        onVideoAdded={handleVideoAdded}
                        onCancel={() => setCurrentView('overview')}
                    />
                );
            case 'multi-upload':
                return (
                    <MultiVideoUploader
                        categories={categories}
                        onVideoAdded={handleMultiUploadVideoAdded}
                        onCancel={() => setCurrentView('overview')}
                    />
                );
            case 'overview':
            default:
                return (
                    <div className="academy-overview">
                        <div className="academy-stats">
                            <div className="academy-stat-card">
                                <h3>Categories</h3>
                                <p className="academy-stat-number">{categories.length}</p>
                            </div>
                            <div className="academy-stat-card">
                                <h3>Videos</h3>
                                <p className="academy-stat-number">{videos.length}</p>
                            </div>
                        </div>

                        <div className="academy-content">
                            <div className="academy-categories-section">
                                <h3>Learning Categories</h3>
                                {categories.length === 0 ? (
                                    <p className="academy-empty-state">No categories created yet. Start by adding your first category.</p>
                                ) : (
                                    <div className="academy-categories-grid">
                                        {categories.map(category => (
                                            <div key={category.id} className="academy-category-card">
                                                <div className="academy-category-image">
                                                    {category.imageUrl ? (
                                                        <img src={category.imageUrl} alt={category.name} />
                                                    ) : (
                                                        <div className="academy-category-image-placeholder">📚</div>
                                                    )}
                                                </div>
                                                <div className="academy-category-content">
                                                    <h4>{category.name}</h4>
                                                    <p>{category.description}</p>
                                                    <div className="academy-category-meta">
                                                        <span className={`academy-tier-badge academy-tier-badge-${category.tier || 'team'}`}>
                                                            {(category.tier || 'team').toUpperCase()}
                                                        </span>
                                                        <span className="academy-category-stats">
                                                            {videos.filter(v => v.categoryId === category.id).length} videos
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="academy-category-actions">
                                                    <button
                                                        className="academy-manage-videos-btn"
                                                        onClick={() => handleManageVideos(category)}
                                                        title="Manage Videos"
                                                    >
                                                        ⚙️ Videos
                                                    </button>
                                                    <button
                                                        className="academy-edit-category-btn"
                                                        onClick={() => handleEditCategory(category)}
                                                        title="Edit Category"
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="academy-recent-videos-section">
                                <h3>Recent Videos</h3>
                                {videos.length === 0 ? (
                                    <p className="academy-empty-state">No videos added yet. Start by creating a category and adding videos.</p>
                                ) : (
                                    <div className="academy-videos-list">
                                        {videos.slice(0, 5).map(video => (
                                            <div key={video.id} className="academy-video-item">
                                                <div className="academy-video-thumbnail">
                                                    {video.thumbnailUrl ? (
                                                        <img src={video.thumbnailUrl} alt={video.title} />
                                                    ) : (
                                                        <div className="academy-thumbnail-placeholder">📹</div>
                                                    )}
                                                </div>
                                                <div className="academy-video-info">
                                                    <h5>{video.title}</h5>
                                                    <p>{video.description}</p>
                                                    <span className="academy-video-category">
                                                        {categories.find(c => c.id === video.categoryId)?.name || 'Unknown Category'}
                                                    </span>
                                                    <span className="academy-video-views">👁 {video.viewCount || 0} views</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
        }
    };

    if (loading) {
        return (
            <div className="academy-loading">
                <p>Loading Academy...</p>
            </div>
        );
    }

    if (!auth.currentUser) {
        return (
            <div className="academy-error">
                <p>Please log in to access the Academy.</p>
            </div>
        );
    }

    return (
        <div className="academy-container">
            <div className="academy-header">
                <h2>Learning Academy</h2>
                <div className="academy-actions">
                    <button 
                        className={`academy-btn ${currentView === 'overview' ? 'active' : ''}`}
                        onClick={() => setCurrentView('overview')}
                    >
                        Overview
                    </button>
                    <button 
                        className={`academy-btn ${currentView === 'add-category' ? 'active' : ''}`}
                        onClick={() => setCurrentView('add-category')}
                    >
                        Add Category
                    </button>
                    <button 
                        className={`academy-btn ${currentView === 'add-video' ? 'active' : ''}`}
                        onClick={() => setCurrentView('add-video')}
                        disabled={categories.length === 0}
                    >
                        Add Video
                    </button>
                    <button 
                        className={`academy-btn ${currentView === 'multi-upload' ? 'active' : ''}`}
                        onClick={() => setCurrentView('multi-upload')}
                        disabled={categories.length === 0}
                    >
                        Multi Upload
                    </button>
                </div>
            </div>

            <div className="academy-content-wrapper">
                {renderCurrentView()}
            </div>

            {/* Video Management Modal */}
            {managingCategory && (
                <VideoManagementModal
                    category={managingCategory}
                    videos={videos}
                    categories={categories}
                    onClose={() => setManagingCategory(null)}
                    onVideosUpdated={handleVideosUpdated}
                />
            )}

            {/* Category Management Modal */}
            {editingCategory && (
                <CategoryManagementModal
                    category={editingCategory}
                    onClose={() => setEditingCategory(null)}
                    onCategoryUpdated={handleCategoryUpdated}
                />
            )}
        </div>
    );
}

export default Academy;
